<template lang="html">
  <section class="m-search-box">
    <div
      class="m-search-box"
      :id="id"
      v-clickoutside="onClickedOutside"
      @keydown.esc.stop="onEsc"
    >
      <div class="m-search-box">
        <label v-if="label" class="m-text-input__label">{{ label }}</label>

        <multi-selection-items
          :is-open="showResultsList"
          :list-items="initialSelectedItems"
          :is-read-only="isReadOnly"
          :has-icon="hasIcon"
          @on-up-arrow-pressed="onUpArrowPressed"
          @on-down-arrow-pressed="onDownArrowPressed"
          @on-tab-pressed="closeDropdown"
          @on-multi-select-focus="onInputFocus"
          @on-multi-select-blur="onInputBlur"
          @on-multi-item-selected="onSingleSelectPressEnter"
          @on-multi-select-close-options="onMultiSelectCloseOptions"
          @on-multi-select-remove-item="onMultSelectRemoveItem"
        />

        <div v-if="isInputReady" class="m-search-container">
          <text-input
            :class="{ 'm-invalid': isInvalidInput }"
            v-model="currentTextValue"
            :type="type"
            :placeholder="placeholderText"
            :select="select"
            :focus="focus"
            :disabled="true"
            :is-multi-select="true"
            :has-right-icon="true"
            @on-up-arrow-pressed="onUpArrowPressed"
            @on-down-arrow-pressed="onDownArrowPressed"
            @on-tab-pressed="closeDropdown"
            @on-text-input-focus="onInputFocus"
            @on-text-input-blur="onInputBlur"
            @on-text-input-change="onInputChanged"
            @on-multi-select-press-enter="onMultiSelectPressEnter"
            @on-single-select-press-enter="onSingleSelectPressEnter"
            @click="onInputFocus"
          />
          <svg class="m-input-search-icon">
            <use
              xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#search"
            ></use>
          </svg>
        </div>
      </div>
      <div ref="resultList" class="m-result-list" v-if="showResultsList">
        <ul>
          <li
            class="m-item"
            :class="getClassForItem(item)"
            v-for="(item, index) in updateList"
            :key="item.Id"
          >
            <search-box-list-item
              class="m-list-option-text"
              :item-id="`${id}-${index}`"
              :box-type="boxType"
              :label-only="labelOnly"
              :has-des="hasDes"
              :list-item="item"
              :exp="exp"
              :has-icon="hasIcon"
              :is-multi-select="true"
              :is-selected="isSelected(item)"
              @on-search-box-item-selected="onSearchBoxItemSelected"
              @on-search-box-item-deselected="onMultSelectRemoveItem"
            />
          </li>
        </ul>
      </div>
      <div class="operand-fields">
        <div
          class="field-select"
          v-for="field in operandFields"
          :key="field.id"
        >
          <text-input
            :id="'engine-field-input-' + field.id"
            :value="field.userLabel || field.as"
            :focus="field.isEmpty"
            type="text"
            @on-text-input-change="onFieldInputChanged"
          />

          <field-with-operand
            :fields="updateList"
            :info="field"
            @on-operand-input-delete="onOperandInputDelete"
            @on-op-update="onOpUpdate"
            @on-arg-change="onArgChange"
          ></field-with-operand>

          <div class="delete-wrapper">
            <svg
              class="delete-row-icon"
              tabindex="0"
              @keydown.enter.stop="deleteField(field.id)"
              @click="deleteField(field.id)"
            >
              <use
                xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#delete"
              ></use>
            </svg>
          </div>
        </div>
      </div>
      <div class="add-btn-container">
        <button ref="addFieldBtn" class="modal-btn" @click="addField">
          {{ addText }}
        </button>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import debounce from "lodash.debounce";
  import TextInput from "@/components/TextInput";
  import SearchBoxListItem from "@/components/SearchBoxListItem";
  import MultiSelectionItems from "@/components/MultiSelectionItems";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";
  import {mapState} from "vuex";
  import { v4 as uuidv4 } from "uuid";
  import FieldWithOperand from "@/components/FieldWithOperand";

  export default  {
    name: 'search-box-with-calculation',
    components: {
      TextInput,
      FieldWithOperand,
      SearchBoxListItem,
      MultiSelectionItems
    },
    props: {
      /**
       * input properties
       * @values id, type, name, placeholder, value, label, list
       */
      id: {
        type: String
      },
      type: {
        type: String,
        default: "text"
      },
      name: {
        type: String
      },
      placeholder: {
        type: String
      },
      value: {
        type: Object
      },
      values: {
        type: Array
      },
      multiSelect: {
        type: Boolean,
        default: false
      },
      textValue: {
        type: String
      },
      label: {
        type: String
      },
      list: {
        type: Array
      },
      boxType: {
        type: String
      },
      hasIcon: {
        type: Boolean
      },
      labelOnly: {
        type: Boolean
      },
      hasDes: {
        type: Boolean
      },
      calculatedFields: {
        type: Array
      },
      inputType: {
        type: String
      },
      isInvalid: {
        type: Boolean
      },
      isReadOnly: {
        type: Boolean
      },
      isFirstInput: {
        type: Boolean
      }
    },
    data () {
      return {
        updateList: [],
        selectedItem: null,
        selectedListItems: [],
        initialSelectedItems: [],
        exp: "",
        showResultsList: false,
        isInvalidInput: false,
        currentTextValue: null,
        select: false,
        focus: false,
        selectedId: -1,
        isInputInFocus: false,
        isFocusReady: false,
        operandFields: [],
        initialOperands: []
      }
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      displaySelectedSingleItem() {
        return this.selectedItem && !this.selectedItem.isEmpty;
      },
      displayForMultiSelect() {
        return this.isInputInFocus || this.showResultsList;
      },
      isInputReady() {
        return this.displayForMultiSelect && this.isFocusReady;
      },
      selectedSet() {
        return new Set(this.selectedListItems.map(e => e.name));
      },
      addText() {
        return this.$t("add");
      },
      placeholderText() {
        if (typeof this.placeholder === 'string' || this.placeholder instanceof String) {
          return this.placeholder;
        } else {
          return this.getMessageText(this.messages.SEARCH);
        }
      }
    },
    watch: {
      textValue: function() {
        this.currentTextValue = "";
        this.$nextTick(() => {
          this.currentTextValue = this.textValue;
        });
      },
      value: {
        handler() {
          this.selectedItem = this.value;
        },
        immediate: true
      },
      values: {
        handler() {
          this.initialSelectedItems = this.values? [...this.values] : [];
          this.selectedListItems = this.values? [...this.values] : [];
        },
        immediate: true
      },
      list: {
        handler() {
          this.setList();
        },
        immediate: true,
        deep: true
      },
      calculatedFields: {
        handler() {
          this.setCalFields();
        },
        immediate: true,
        deep: true
      },
      isInvalid: function() {
        this.setInvalidValue();
      }
    },
    mounted() {
      this.focus = true;
      this.isFocusReady = true;
      this.setList();
    },
    methods: {
      getClassForItem(item) {
        if (this.selectedId < 0) {
          return "";
        }

        let result;
        const visibleList = this.updateList.filter(e => e.visible);
        if (item === visibleList[this.selectedId] && this.isSelected(visibleList[this.selectedId])) {
          result = "selected-item-on-hover";
        } else if (item === visibleList[this.selectedId]) {
          result = 'item-on-hover-highlight';
        } else {
          result = ""
        }

        return result;
      },
      selectAnItem(id) {
        if (this.$refs.resultList) {
          this.selectedId = id;
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[id]) {
            filteredList[id].scrollIntoView(false);
          }
        }
      },
      onUpArrowPressed() {
        if (this.selectedId > 0) {
          this.selectAnItem(this.selectedId - 1);
        }
      },
      onDownArrowPressed() {
        const visibleList = this.updateList.filter(e => e.visible);
        if (this.selectedId < visibleList.length - 1) {
          this.selectAnItem(this.selectedId + 1);
        }
      },
      onEsc() {
        this.closeDropdown();
      },
      onClickedOutside() {
        if (!this.isInputInFocus) {
          this.closeDropdown();
        }
      },
      closeDropdown() {
        this.showResultsList = false;
        this.isInputInFocus = false;
      },
      setList() {
        this.updateList = [];
        if(this.list) {
          for(const el of this.list) {
            const value = {...el};
            this.updateList.push(value);
          }
        }
      },
      setCalFields() {
        let reformatted = []
        if (this.calculatedFields) {
          for(const cf of this.calculatedFields) {
            const value = {...cf};
            reformatted.push(value);
          }
          this.operandFields = reformatted;
        }
      },
      setInvalidValue() {
        this.isInvalidInput = this.isInvalid;
      },
      closeSelected() {
        this.selectedItem = this.id ? {searchBoxId: this.id, isEmpty: true} : null;
        this.$emit("on-search-box-item-selected", this.selectedItem);
      },
      onArgChange(id, updatedList) {
        let target = this.operandFields.find(op => {
          return op.id === id;
        });
        target.arguments = updatedList;
        this.$emit("on-cal-fields-updated", this.operandFields, this.id);
      },
      onOpUpdate(opType, id, propName, field, index) {
        let i = this.operandFields.findIndex(op => {
          return op.id === id;
        });
        if (opType) {
          this.operandFields[i].type = opType;
          if (propName === "arguments") {
            if (!this.operandFields[i][propName]) {
              this.operandFields[i][propName] = [];
            }
            this.operandFields[i][propName][index] = field;
          } else {
            this.operandFields[i][propName] = field;
          }
        } else {
          this.operandFields[i] = {
            id: this.operandFields[i].id,
            as: this.operandFields[i].as || ""
          }
        }
        this.$emit("on-cal-fields-updated", this.operandFields, this.id);
      },
      onOperandInputDelete(id) {
        this.operandFields = this.operandFields.filter(f => {
          return f.id !== id;
        })
      },
      addField() {
        this.operandFields.push({
          id: uuidv4(),
          as: ""
        });
        this.$emit("on-cal-fields-updated", this.operandFields, this.id);
      },
      onSearchBoxItemSelected(item) {
        if (this.id) {
          item.searchBoxId = this.id;
        }
        this.setMultiSelectItem(item);
      },

      onFieldInputChanged(text, inputId) {
        const formattedId = inputId.replace("engine-field-input-", "");
        let target = this.operandFields.find(op => {
          return op.id === formattedId;
        });
        target.as = text;
        this.$emit("on-cal-fields-updated", this.operandFields, this.id);
      },
      onInputFocus() {
        this.isInputInFocus = true;
        if (!this.isReadOnly) {
          this.showResultsList = true;
          this.isInvalidInput = false;
        }
      },
      onInputBlur() {
        this.isInputInFocus = false;
      },
      onInputChanged: debounce(function(val) {
        this.selectedId = -1;
        /**
         * Triggers when model value changes
         * @event on-search-box-change
         */
        if(this.$listeners && this.$listeners["on-search-box-change"]) {
          this.$emit("on-search-box-change", val, this.id || null);
        }
        this.exp = val;
      }, 400),
      setSingleItem(item) {
        this.selectedItem = item;
        this.$emit("on-search-box-item-selected", this.selectedItem);
        this.closeDropdown();
      },
      isSelected(item) {
        return this.selectedSet.has(item.name);
      },
      onMultiSelectCloseOptions() {
        this.showResultsList = false;
      },
      onMultSelectRemoveItem(item) {
        const filterd = this.selectedListItems.filter(e => e.name !== item.name);
        this.$emit("on-search-box-item-selected", filterd, this.id);
      },
      setMultiSelectItem(item) {
        this.selectedListItems.push(item);
        this.$emit("on-search-box-item-selected", this.selectedListItems, this.id);
        this.setHightlight();
      },
      setHightlight() {
        this.select = true;
        this.$nextTick(() => {
          // reset to trigger select next time
          this.select = false;
        });
      },
      onMultiSelectPressEnter() {
        const visibleList = this.updateList.filter(e => e.visible);
        const item = visibleList[this.selectedId];
        if(item) {
          if(this.isSelected(item)) {
            this.onMultSelectRemoveItem(item);
          } else {
            this.setMultiSelectItem(item);
          }
          this.setHightlight();
        }
      },
      getUtilityIcon(item) {
        return iconsUri.getUtilityIconByType(item.type);
      },
      onSingleSelectPressEnter() {
        if (this.selectedId >= 0) {
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[this.selectedId] && filteredList[this.selectedId].children[0]) {
            filteredList[this.selectedId].children[0].click();
          }
        }
      },
      deleteField(id) {
        let component = this;
        this.$bvModal
          .msgBoxConfirm(this.getMessageText(this.messages.ARE_YOU_SURE), {
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.okText,
            cancelTitle: this.cancelText,
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          })
          .then(value => {
            if (value) {
              component.operandFields = component.operandFields.filter(
                field => {
                  return field.id !== id;
                }
              );
              component.$emit("on-cal-fields-updated", this.operandFields, this.id);
            }
          })
          .catch(err => {
            console.error(err);
            // An error occurred
          });
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .m-icon-selected {
    padding-left: 4px;
  }
  .icon-margin {
    margin-bottom: 0.25rem;
  }
  .m-selection-label {
    display: inline-block;
    height: 24px;
  }
  .m-content {
    display: inline;
  }
  .selected-item-on-hover {
    background-color: #ff6a6a;
  }
  .item-on-hover-highlight {
    background-color: var(--HEADER_GRAY);
  }

  .operand-fields {
    margin-top: 1rem;
  }

  .field-select {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    .m-text-input__container {
      padding-right: 0.5rem;
    }
  }

  .delete-wrapper {
    margin: 0.35rem 0 0 0.5rem;
    .delete-row-icon {
      height: 1.25rem;
      width: 1.25rem;
      fill: var(--SF_ERROR_RED);
      cursor: pointer;
    }
  }
</style>
